import React, { useMemo } from 'react';
import { Icon } from '@iconify/react';
import {
    AttributeDefinitionFieldType,
    ZinantisServiceState,
} from '../../../types/zitoneoServicesTypes';
import ServiceDescription from './ServiceDescription';
import ServiceException from './ServiceException';
import { formatDate } from '../../../../../utils/formatDates';
import { useZitoneoPreview } from '../../../contexts/ZitoneoContentContext';
import useTranslate from '../../../../../customHooks/useTranslate';

interface ServiceSectionProps {
    service: ZinantisServiceState;
    index?: number;
    scope?: 'hotel' | 'room';
}

const ServiceSection = (props: ServiceSectionProps) => {
    const { service, index, scope = 'hotel' } = props;
    const Heading = scope === 'room' ? 'h5' : 'h6';
    const translate = useTranslate();
    const { visibleLanguage } = useZitoneoPreview();

    const renderAttributeValue = (
        fieldType: AttributeDefinitionFieldType,
        value: string
    ) => {
        switch (fieldType) {
            case AttributeDefinitionFieldType.BOOLEAN:
                return value === 'True' ? translate('yes') : translate('no');
            case AttributeDefinitionFieldType.DATE:
                return formatDate(value, visibleLanguage?.Code);
            case AttributeDefinitionFieldType.NUMBER:
            case AttributeDefinitionFieldType.STRING:
            default:
                return value;
        }
    };

    const definitions = useMemo(() => {
        return service.AttributeDefinitions?.filter(attributeDef =>
            service?.AttributeValues?.some(
                val =>
                    val.IdAttributeDefinition ===
                    attributeDef.IdAttributeDefinition
            )
        );
    }, [service.AttributeDefinitions, service.AttributeValues]);

    return (
        <li className='service-section__content-item' key={service.Id}>
            <div className='service-content-item__header'>
                <Icon
                    icon='icon-park-outline:dot'
                    className='service-content-item__icon'
                />

                <Heading className='service-content-item__title'>
                    {service.Name
                        ? `${service.Name} - ${service.ServiceType}`
                        : `${service.ServiceType}`}
                    {!service.Name && index && (
                        <span
                            style={{
                                fontWeight: '400',
                                fontSize: '16px',
                                color: 'var(--grey-600)',
                            }}
                        >
                            #{index}
                        </span>
                    )}
                </Heading>
            </div>

            {service.Exceptions.length > 0 &&
                service.Exceptions.map(exception => (
                    <ServiceException
                        key={exception.Id}
                        exception={exception}
                    />
                ))}
            {definitions?.length ? (
                <ul className='service-content-item__attributes'>
                    {definitions?.map(attributeDef => {
                        const attributeValues =
                            service?.AttributeValues?.filter(
                                val =>
                                    val.IdAttributeDefinition ===
                                    attributeDef.IdAttributeDefinition
                            );
                        if (attributeDef.Unique) {
                            return (
                                <li
                                    className='service-content-item__attribute'
                                    key={attributeDef.Id}
                                >
                                    <div className='service-content-attribute__label'>
                                        {attributeDef?.Name}
                                    </div>
                                    <div className='service-content-attribute__value'>
                                        {renderAttributeValue(
                                            attributeDef.FieldType,
                                            attributeValues?.[0]?.Value || ''
                                        )}
                                    </div>
                                </li>
                            );
                        } else {
                            return (
                                <li
                                    className='service-content-item__attribute'
                                    key={attributeDef.Id}
                                >
                                    <div className='service-content-attribute__label'>
                                        {attributeDef?.Name}
                                    </div>
                                    <div className='service-content-attribute__values'>
                                        {attributeValues?.map(val => (
                                            <p
                                                className='service-content-attribute__value'
                                                key={val.Id}
                                            >
                                                {renderAttributeValue(
                                                    attributeDef.FieldType,
                                                    val.Value || ''
                                                )}
                                            </p>
                                        ))}
                                    </div>
                                </li>
                            );
                        }
                    })}
                </ul>
            ) : (
                ''
            )}

            {service.Description ? (
                <ServiceDescription description={service.Description || ''} />
            ) : null}
        </li>
    );
};

export default ServiceSection;
